<template>
  <!-- eslint-disable vue/valid-v-slot -->
  <v-container fluid class="delivery-order-checklist" v-if="order">

    <!------------------>
    <!-- Desktop View -->
    <!------------------>
    <div class="item-checklist-desktop">
      <v-data-table v-if="hasOrderedItems"
          class="list-ordered-items"
          hide-default-footer
          disable-pagination
          :headers="headers"
          :items="orderedItems">

          <!-- product batch number or ID (for check) -->
          <template v-slot:item.product_id="{ item }">
            <v-checkbox
                v-if="!showChecked"
                :label="getProductLabel(item)"
                :value="item.product_id.toString()"
                v-model="confirmedItems" />

            <!-- (only show completed icon when previously confirmed) -->
            <div class="d-flex align-center" style="height:90px;" v-else>
              <v-icon color="green" style="font-size:24px;" class="mr-2">mdi-check-circle</v-icon>
              {{ getProductLabel(item) }}
            </div>
          </template>

          <!-- batch number -->
          <template v-slot:item.batch_number="{ item }">
            <span>{{ item.batch_number }}</span>
          </template>

          <!-- quantity -->
          <template v-slot:item.quantity="{ item }">
            <span class="delivery_date">{{ item.quantity }}</span>
          </template>
      </v-data-table>

      <div v-else>
        (No ordered items)
      </div>
    </div>

    <!----------------->
    <!-- Mobile View -->
    <!----------------->
    <div class="item-checklist-mobile">
      <v-container>
        <v-row v-for="(item, index) in orderedItems" :key="index">
          <v-col cols="5" class="ma-0 pa-0">
            <div v-if="showChecked" class="mt-12">
              <v-icon color="green" class="pl-1 pb-2">mdi-check-circle</v-icon>
            </div>
            <v-checkbox v-else
                class="pl-1"
                :value="item.product_id.toString()"
                v-model="confirmedItems" />
            <!-- Show batch number if available -->
            <template v-if="item.batch_number">
              <div class="pb-2">Batch #</div>
              <div style="font-size:17px;">
                {{ item.batch_number }}
              </div>
            </template>
            <!-- Otherwise, show product ID -->
            <template v-else>
              <div class="pb-2">Product ID</div>
              <div style="font-size:17px;">
                {{ item.product_id }}
              </div>
            </template>
          </v-col>
          <v-col cols="7">
            <v-divider class="my-2" />
            <div class="pb-2">
              {{ item.product_label }}
            </div>
            <div style="font-size:16px;font-weight:700;" class="mt-4">
              {{ item.quantity }}
            </div>
            <v-divider class="my-2" />
          </v-col>
        </v-row>
      </v-container>
    </div>

    <!-- Actions -->
    <div class="mt-6">
      <!-- Confirmation timestamp -->
      <template v-if="wasPreviouslyConfirmed">
        <div class="ml-2">
          <span style="color:#9c9c9c;font-size:1.0rem;font-style:italic" class="mr-3">Items were validated by the driver:</span>
          <span style="font-size:1.08rem;color:#f8f8f8;">{{ itemDateFormat(order.items_checked_at) }}</span>
        </div>
        <v-btn large text class="mt-6" color="primary" @click="close">
          Close
        </v-btn>
      </template>

      <template v-else>
        <v-btn text class="mr-8" color="primary" @click="close">
          Close
        </v-btn>
        <v-btn large color="primary" style="width:120px;" :disabled="!areAllItemsConfirmed" @click="complete" v-if="!checkOffMode">
          Complete
        </v-btn>
      </template>

    </div>
  </v-container>

</template>

<script>
/* eslint-disable no-unneeded-ternary */
export default {
  name: 'DeliveryOrderChecklist',

  props: {
    order: {
      type: Object,
      required: true
    },
    checkOffMode: {
      type: Boolean,
      required: false
    }
  },

  data () {
    return {
      headers: [
        { text: 'Batch Number', align: 'start', value: 'product_id' },
        // { text: 'Batch Number', align: 'start', value: 'batch_number' },
        { text: 'Product Name', value: 'product_label', width: '45%' },
        // { text: 'Strain', value: 'product_strain' },
        { text: 'Quantity', value: 'quantity', width: '14%' }
      ],
      isDataLoading: false,
      orderDetails: null,
      confirmedItems: []
    }
  },

  computed: {
    orderedItems () {
      // Sort by product name
      return (this.orderDetails && this.orderDetails.item_data)
        ? this.$root.utils.orderBy(this.orderDetails.item_data.items, ['product_label'], ['asc'])
        : []
    },
    hasOrderedItems () {
      return this.orderedItems.length > 0
    },
    wasPreviouslyConfirmed () {
      return (this.order.items_checked_at)
        ? true
        : false
    },
    showChecked () {
      return (this.checkOffMode) ? false : this.wasPreviouslyConfirmed
    },
    areAllItemsConfirmed () {
      return this.confirmedItems.length === this.orderedItems.length
    }
  },

  async beforeMount () {
    // Load order details
    await this.loadOrderDetails()
  },

  methods: {
    itemDateFormat (date) {
      return this.$root.utils.dateFormat(date, 'MMMM D, YYYY - h:mm a z')
    },

    getProductLabel (item) {
      return item.batch_number || item.product_id.toString()
    },

    strainItems (strainArray) {
      return (strainArray) ? strainArray.toString() : ''
    },

    // Emit closed event
    close () {
      this.$emit('closed')
    },

    // Emit completed event
    complete () {
      this.$emit('completed')
    },

    async loadOrderDetails () {
      try {
        this.orderDetails = await this.$store.dispatch('sh/dataApi', {
          method: 'get',
          uri: `order/${this.order.id}/details`
        })
      } catch (err) {
        console.log(`Error retrieving order details for order ${this.order.id} =>`, err)
      }
    }
  }
}
</script>

<style scoped>

@media screen and (max-width: 475px) {
  .item-checklist-desktop {
    display: none;
  }

  .item-checklist-mobile {
    display: block;
  }
}

@media screen and (min-width: 476px) {
  .item-checklist-desktop {
    display: block;
    margin-bottom: 24px;
  }

  .item-checklist-mobile {
    display: none;
  }
}

</style>
